import { useState } from 'react';
import {
  useKeycloakConfig,
  useSaveKeycloakConfig,
  useTestConnection,
  useFetchKeycloakUsers,
  useExecuteSync,
  useSyncStatus,
  useUpdateRoleMapping,
} from '../../api/hooks/keycloakSync.hook';
import type {
  KeycloakUser,
  SyncResult,
} from '../../api/services/keycloakSync';

// ── Setup Form ──────────────────────────────────────────────────────

const SetupForm = ({ existing }: { existing?: any }) => {
  const save = useSaveKeycloakConfig();
  const testConn = useTestConnection();
  const [serverUrl, setServerUrl] = useState(existing?.server_url ?? '');
  const [realm, setRealm] = useState(existing?.realm ?? '');
  const [clientId, setClientId] = useState(existing?.client_id ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await save.mutateAsync({
        server_url: serverUrl,
        realm,
        client_id: clientId,
        client_secret: clientSecret,
      });
      setSaved(true);
    } catch {
      setError('Failed to save configuration.');
    }
  };

  const handleTest = async () => {
    setError(null);
    try {
      const result = await testConn.mutateAsync();
      if (result.ok) {
        setError(null);
        alert(
          `Connection successful! Found ${result.user_count} users and ${result.group_count} groups in realm "${result.realm}".`,
        );
      } else {
        setError(result.error ?? 'Connection test failed.');
      }
    } catch {
      setError('Connection test failed.');
    }
  };

  return (
    <form
      onSubmit={handleSave}
      className="bg-white rounded-lg border border-neutral-200 p-4 mb-6"
    >
      <h2 className="text-sm font-medium text-neutral-700 mb-3">
        Keycloak Connection
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-neutral-500 mb-1">
            Server URL
          </label>
          <input
            type="url"
            value={serverUrl}
            onChange={(e) => setServerUrl(e.target.value)}
            placeholder="https://keycloak.example.com"
            className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-1">Realm</label>
          <input
            type="text"
            value={realm}
            onChange={(e) => setRealm(e.target.value)}
            placeholder="my-realm"
            className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-1">
            Client ID
          </label>
          <input
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder="my-client"
            className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-1">
            Client Secret
          </label>
          <input
            type="password"
            value={clientSecret}
            onChange={(e) => setClientSecret(e.target.value)}
            placeholder={existing ? '(unchanged)' : 'client-secret'}
            className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
            required={!existing}
          />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button
          type="submit"
          disabled={save.isLoading}
          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {save.isLoading ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          type="button"
          onClick={handleTest}
          disabled={testConn.isLoading || !serverUrl || !realm || !clientId}
          className="px-4 py-1.5 text-sm font-medium text-neutral-700 bg-neutral-100 rounded hover:bg-neutral-200 disabled:opacity-50 transition-colors"
        >
          {testConn.isLoading ? 'Testing...' : 'Test Connection'}
        </button>
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
      {saved && (
        <p className="mt-2 text-xs text-green-600">Configuration saved.</p>
      )}
    </form>
  );
};

// ── User Sync Panel ─────────────────────────────────────────────────

const UserSyncPanel = () => {
  const fetchUsers = useFetchKeycloakUsers();
  const sync = useExecuteSync();
  const { data: statusData } = useSyncStatus();
  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  const handleFetch = async () => {
    try {
      const result = await fetchUsers.mutateAsync({
        search: search || undefined,
      });
      setUsers(result.users);
      setSelected(new Set());
      setSyncResult(null);
    } catch {
      // error handled by mutation state
    }
  };

  const toggleUser = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === users.filter((u) => !u.already_imported).length) {
      setSelected(new Set());
    } else {
      setSelected(
        new Set(users.filter((u) => !u.already_imported).map((u) => u.id)),
      );
    }
  };

  const handleSync = async () => {
    try {
      const result = await sync.mutateAsync({
        user_ids: Array.from(selected),
      });
      setSyncResult(result);
      // Refresh user list to update imported status
      handleFetch();
    } catch {
      // error handled by mutation state
    }
  };

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden mb-6">
      <div className="px-4 py-3 border-b border-neutral-100">
        <h2 className="text-sm font-medium text-neutral-700">
          Import Users from Keycloak
        </h2>
      </div>

      {/* Search + Fetch */}
      <div className="px-4 py-3 flex gap-3 items-end border-b border-neutral-50">
        <div className="flex-1">
          <label className="block text-xs text-neutral-500 mb-1">
            Search users
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full border border-neutral-200 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
          />
        </div>
        <button
          type="button"
          onClick={handleFetch}
          disabled={fetchUsers.isLoading}
          className="px-4 py-1.5 text-sm font-medium text-neutral-700 bg-neutral-100 rounded hover:bg-neutral-200 disabled:opacity-50 transition-colors"
        >
          {fetchUsers.isLoading ? 'Fetching...' : 'Fetch Users'}
        </button>
      </div>

      {/* User list */}
      {users.length > 0 ? (
        <>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 uppercase tracking-wide border-b border-neutral-50">
                <th className="px-4 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={
                      selected.size ===
                      users.filter((u) => !u.already_imported).length
                    }
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Keycloak Roles</th>
                <th className="px-4 py-2">Suggested Role</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-neutral-50 hover:bg-neutral-50/50"
                >
                  <td className="px-4 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(user.id)}
                      onChange={() => toggleUser(user.id)}
                      disabled={user.already_imported}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-2.5 text-neutral-900">
                    {user.display_name}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-500">
                    {user.email ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-500 text-xs">
                    {user.realm_roles.join(', ') || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-neutral-500 capitalize">
                    {user.suggested_role}
                  </td>
                  <td className="px-4 py-2.5">
                    {user.already_imported ? (
                      <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                        Imported
                      </span>
                    ) : (
                      <span className="text-xs text-neutral-400">
                        Not imported
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Sync button */}
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              type="button"
              onClick={handleSync}
              disabled={selected.size === 0 || sync.isLoading}
              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {sync.isLoading
                ? 'Syncing...'
                : `Import ${selected.size} user${selected.size !== 1 ? 's' : ''}`}
            </button>
            <span className="text-xs text-neutral-400">
              {users.filter((u) => u.already_imported).length} already imported
            </span>
          </div>
        </>
      ) : fetchUsers.isLoading ? (
        <div className="px-4 py-8 text-center text-sm text-neutral-400">
          Fetching users from Keycloak...
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-sm text-neutral-400">
          Click &quot;Fetch Users&quot; to load users from Keycloak.
        </div>
      )}

      {/* Sync result */}
      {syncResult && (
        <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50">
          <p className="text-sm text-neutral-700">
            Sync complete: <strong>{syncResult.synced}</strong> imported,{' '}
            <strong>{syncResult.skipped}</strong> skipped
            {syncResult.errors.length > 0 && (
              <span className="text-red-600">
                , {syncResult.errors.length} errors
              </span>
            )}
          </p>
          {syncResult.errors.length > 0 && (
            <ul className="mt-1 text-xs text-red-500 list-disc list-inside">
              {syncResult.errors.map((err, i) => (
                <li key={i}>
                  {err.email ?? err.user_id}: {err.error}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Last sync status */}
      {statusData?.last_sync_at && (
        <div className="px-4 py-2 border-t border-neutral-50 text-xs text-neutral-400">
          Last sync:{' '}
          {new Date(statusData.last_sync_at).toLocaleString()}
          {statusData.last_sync_result && (
            <>
              {' '}— {statusData.last_sync_result.synced} synced,{' '}
              {statusData.last_sync_result.skipped} skipped
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Role Mapping Panel ──────────────────────────────────────────────

const RoleMappingPanel = ({
  roleMapping,
}: {
  roleMapping: Record<string, string>;
}) => {
  const update = useUpdateRoleMapping();
  const [mapping, setMapping] = useState(roleMapping);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    setSaved(false);
    try {
      await update.mutateAsync({ role_mapping: mapping });
      setSaved(true);
    } catch {
      // error in mutation state
    }
  };

  const updateEntry = (key: string, value: string) => {
    setMapping((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div className="bg-white rounded-lg border border-neutral-200 p-4 mb-6">
      <h2 className="text-sm font-medium text-neutral-700 mb-3">
        Role Mapping
      </h2>
      <p className="text-xs text-neutral-500 mb-3">
        Map Keycloak realm roles to local organization roles. The &quot;*&quot;
        entry is the fallback for unmatched roles.
      </p>
      <div className="space-y-2">
        {Object.entries(mapping).map(([kcRole, localRole]) => (
          <div key={kcRole} className="flex items-center gap-3">
            <span className="text-sm text-neutral-600 w-32 font-mono">
              {kcRole}
            </span>
            <span className="text-neutral-400 text-sm">→</span>
            <select
              value={localRole}
              onChange={(e) => updateEntry(kcRole, e.target.value)}
              className="border border-neutral-200 rounded px-3 py-1 text-sm bg-white"
            >
              <option value="admin">Admin</option>
              <option value="member">Member</option>
            </select>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={update.isLoading}
          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {update.isLoading ? 'Saving...' : 'Save Mapping'}
        </button>
        {saved && (
          <span className="text-xs text-green-600">Mapping saved.</span>
        )}
      </div>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────

export const KeycloakSyncPage = () => {
  const { data, isLoading } = useKeycloakConfig();

  if (isLoading) {
    return (
      <div className="max-w-4xl">
        <h1 className="text-lg font-semibold text-neutral-900 mb-6">
          Keycloak SSO Sync
        </h1>
        <div className="text-sm text-neutral-400">Loading...</div>
      </div>
    );
  }

  const isConfigured = data && 'id' in data;
  const config = isConfigured ? data : null;

  return (
    <div className="max-w-4xl">
      <h1 className="text-lg font-semibold text-neutral-900 mb-2">
        Keycloak SSO Sync
      </h1>
      <p className="text-sm text-neutral-500 mb-6">
        Connect your Keycloak realm to import users in bulk. Users imported here
        can log in via SSO and will be automatically assigned to your
        organization.
      </p>

      <SetupForm existing={config} />

      {isConfigured && (
        <>
          <RoleMappingPanel roleMapping={config!.role_mapping} />
          <UserSyncPanel />
        </>
      )}
    </div>
  );
};
